import { trackMixpanel } from '@utils/mixpanelClient'
import {
  endVirtualTourAnalyticsSession,
  ensureVirtualTourAnalyticsSession,
  getVirtualTourElapsedMetrics,
  hasVirtualTourMilestoneFired,
  markVirtualTourMilestoneFired,
  readVirtualTourAnalyticsSession,
  recordVirtualTourStepTimestamp,
  recordVirtualTourTimestamp,
  type VirtualTourAnalyticsMilestone,
  type VirtualTourAnalyticsTimestampKey,
  type VirtualTourAnalyticsSession,
} from './analyticsSession'

type VirtualTourEventProperties = Record<string, unknown>

const VIRTUAL_TOUR_FEATURE = {
  feature: 'virtual-tour',
  version: 'v1',
} as const

const VIRTUAL_TOUR_EVENTS = {
  menuExpanded: 'virtual_tour_menu_expanded',
  selected: 'virtual_tour_selected',
  startRequested: 'virtual_tour_start_requested',
  started: 'virtual_tour_started',
  startFailed: 'virtual_tour_start_failed',
  stepViewed: 'virtual_tour_step_viewed',
  stepAdvanced: 'virtual_tour_step_advanced',
  completed: 'virtual_tour_completed',
  dismissed: 'virtual_tour_dismissed',
  abandoned: 'virtual_tour_abandoned',
} as const

const START_FAILURE_REASONS = {
  missingTour: 'missing_tour',
  noSteps: 'no_steps',
  contentfulError: 'contentful_error',
  themeIdUnresolved: 'theme_id_unresolved',
} as const

export type StartFailureReason =
  (typeof START_FAILURE_REASONS)[keyof typeof START_FAILURE_REASONS]

const DISMISS_ACTIONS = {
  skip: 'skip',
  close: 'close',
  escape: 'escape',
} as const

export type DismissAction = (typeof DISMISS_ACTIONS)[keyof typeof DISMISS_ACTIONS]

const ABANDON_REASONS = {
  browserBack: 'browser_back',
  externalNavigation: 'external_navigation',
  targetNotFound: 'target_not_found',
  noResolvableSteps: 'no_resolvable_steps',
} as const

export type AbandonReason =
  (typeof ABANDON_REASONS)[keyof typeof ABANDON_REASONS]

const ADVANCE_TYPES = {
  next: 'next',
  back: 'back',
  interactiveClick: 'interactive_click',
  route: 'route',
  done: 'done',
} as const

export type AdvanceType = (typeof ADVANCE_TYPES)[keyof typeof ADVANCE_TYPES]

const buildBaseProperties = (
  session?: VirtualTourAnalyticsSession | null,
): VirtualTourEventProperties => ({
  feature: VIRTUAL_TOUR_FEATURE.feature,
  virtual_tour_version: VIRTUAL_TOUR_FEATURE.version,
  virtual_tour_session_id: session?.sessionId ?? null,
  tour_slug: session?.tourSlug ?? null,
  tour_label: session?.tourLabel ?? null,
  tour_step_count: session?.tourStepCount ?? null,
  launch_id: session?.launchId ?? null,
  contentful_enabled: session?.contentfulEnabled ?? null,
  theme_edit_code_flag: session?.themeEditCodeFlag ?? null,
  has_theme_id: session?.hasThemeId ?? null,
})

const trackMilestone = (
  event: string,
  properties: VirtualTourEventProperties,
  milestone: VirtualTourAnalyticsMilestone,
  timestamp?: VirtualTourAnalyticsTimestampKey,
): boolean => {
  if (hasVirtualTourMilestoneFired(milestone)) {
    return false
  }

  const sent = trackMixpanel(event, properties)
  if (!sent) return false

  markVirtualTourMilestoneFired(milestone)
  if (timestamp) {
    recordVirtualTourTimestamp(timestamp)
  }

  return true
}

export const virtualTourAnalytics = {
  menuExpanded: (availableTourCount: number, availableTourSlugs: string[]) => {
    trackMixpanel(VIRTUAL_TOUR_EVENTS.menuExpanded, {
      feature: VIRTUAL_TOUR_FEATURE.feature,
      virtual_tour_version: VIRTUAL_TOUR_FEATURE.version,
      available_tour_count: availableTourCount,
      available_tour_slugs: availableTourSlugs,
    })
  },

  selected: (
    tourSlug: string,
    tourLabel: string,
    tourOrder: number,
    availableTourSlugs: string[],
  ) => {
    trackMixpanel(VIRTUAL_TOUR_EVENTS.selected, {
      feature: VIRTUAL_TOUR_FEATURE.feature,
      virtual_tour_version: VIRTUAL_TOUR_FEATURE.version,
      tour_slug: tourSlug,
      tour_label: tourLabel,
      tour_order: tourOrder,
      available_tour_slugs: availableTourSlugs,
    })
  },

  startRequested: (tourSlug: string, tourLabel: string) => {
    trackMixpanel(VIRTUAL_TOUR_EVENTS.startRequested, {
      feature: VIRTUAL_TOUR_FEATURE.feature,
      virtual_tour_version: VIRTUAL_TOUR_FEATURE.version,
      tour_slug: tourSlug,
      tour_label: tourLabel,
    })
  },

  started: (
    context: {
      tourSlug: string
      tourLabel: string
      tourStepCount: number
      launchId: string
      contentfulEnabled: boolean
      themeEditCodeFlag: boolean
      hasThemeId: boolean
    },
  ) => {
    const session = ensureVirtualTourAnalyticsSession(context)
    const elapsed = getVirtualTourElapsedMetrics(session)

    trackMilestone(
      VIRTUAL_TOUR_EVENTS.started,
      {
        ...buildBaseProperties(session),
        ...elapsed,
      },
      'started',
      'started',
    )
  },

  startFailed: (
    reason: StartFailureReason,
    context: {
      tourSlug?: string
      tourLabel?: string
    },
  ) => {
    trackMixpanel(VIRTUAL_TOUR_EVENTS.startFailed, {
      feature: VIRTUAL_TOUR_FEATURE.feature,
      virtual_tour_version: VIRTUAL_TOUR_FEATURE.version,
      tour_slug: context.tourSlug ?? null,
      tour_label: context.tourLabel ?? null,
      failure_reason: reason,
    })
  },

  stepViewed: (
    stepIndex: number,
    stepKey: string,
    stepTitle: string,
    route: string,
  ) => {
    const session = readVirtualTourAnalyticsSession()
    if (!session) return

    const milestone: VirtualTourAnalyticsMilestone = `step_viewed:${stepIndex}`
    if (hasVirtualTourMilestoneFired(milestone)) return

    recordVirtualTourStepTimestamp(stepIndex)
    const elapsed = getVirtualTourElapsedMetrics(session, stepIndex)

    trackMilestone(
      VIRTUAL_TOUR_EVENTS.stepViewed,
      {
        ...buildBaseProperties(session),
        step_index: stepIndex,
        step_key: stepKey,
        step_title: stepTitle,
        route,
        ...elapsed,
      },
      milestone,
    )
  },

  stepAdvanced: (
    stepIndex: number,
    stepKey: string,
    stepTitle: string,
    route: string,
    advanceType: AdvanceType,
  ) => {
    const session = readVirtualTourAnalyticsSession()
    if (!session) return

    const elapsed = getVirtualTourElapsedMetrics(session, stepIndex)

    trackMixpanel(VIRTUAL_TOUR_EVENTS.stepAdvanced, {
      ...buildBaseProperties(session),
      step_index: stepIndex,
      step_key: stepKey,
      step_title: stepTitle,
      route,
      advance_type: advanceType,
      ...elapsed,
    })
  },

  completed: () => {
    const session = readVirtualTourAnalyticsSession()
    if (!session) return

    const elapsed = getVirtualTourElapsedMetrics(session)

    trackMilestone(
      VIRTUAL_TOUR_EVENTS.completed,
      {
        ...buildBaseProperties(session),
        ...elapsed,
      },
      'completed',
      'completed',
    )
    endVirtualTourAnalyticsSession()
  },

  dismissed: (
    action: DismissAction,
    stepIndex?: number,
    stepKey?: string,
  ) => {
    const session = readVirtualTourAnalyticsSession()
    if (!session || hasVirtualTourMilestoneFired('dismissed')) return

    const elapsed = getVirtualTourElapsedMetrics(session, stepIndex)

    trackMilestone(
      VIRTUAL_TOUR_EVENTS.dismissed,
      {
        ...buildBaseProperties(session),
        step_index: stepIndex ?? null,
        step_key: stepKey ?? null,
        dismiss_action: action,
        ...elapsed,
      },
      'dismissed',
      'dismissed',
    )
    endVirtualTourAnalyticsSession()
  },

  abandoned: (
    reason: AbandonReason,
    context: {
      stepIndex?: number
      stepKey?: string
      missingSelector?: string
    } = {},
  ) => {
    const session = readVirtualTourAnalyticsSession()
    if (!session || hasVirtualTourMilestoneFired('abandoned')) return

    const elapsed = getVirtualTourElapsedMetrics(session, context.stepIndex)

    trackMilestone(
      VIRTUAL_TOUR_EVENTS.abandoned,
      {
        ...buildBaseProperties(session),
        step_index: context.stepIndex ?? null,
        step_key: context.stepKey ?? null,
        missing_selector: context.missingSelector ?? null,
        abandon_reason: reason,
        ...elapsed,
      },
      'abandoned',
      'abandoned',
    )
    endVirtualTourAnalyticsSession()
  },

  readSession: readVirtualTourAnalyticsSession,
  isMilestoneFired: hasVirtualTourMilestoneFired,
}
